import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Chip, ChipSize, ChipVariant } from '@/components/Chip/Chip'
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@/components/ResponsiveModal/ResponsiveModal'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { cn } from '@/lib/utils'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import { useRunReportWizardStep } from '@/pages/RunReport/hooks/useRunReportWizardStep'
import {
  EConsentMethod,
  EReportType,
  ERunReportStep,
  ESearchStartedVariant,
} from '@/pages/RunReport/types'
import {
  useSaveComplianceSignedPdfMutation,
  useSendEmailWithComplianceDocumentMutation,
  useStartNewPersonSearchMutation,
} from '@/store/api/RunReport/RunReport.query'
import {
  selectRunReport,
  selectSearchTarget,
} from '@/store/api/RunReport/RunReport.selector'
import {
  setCompanyInfo,
  setFirstStep,
  setReportSetup,
  setSocialMedia,
  setSubjectInfo,
} from '@/store/api/RunReport/RunReport.slice'
import {
  selectSelectedTeam,
  selectSelectedWorkStation,
  selectUserInfo,
} from '@/store/api/Users/Users.selector'
import { base64ToFormData } from '@/utils/binary'
import { filterEmptyFields } from '@/utils/filterEmptyFields'
import { inferComplianceJurisdiction } from '@/utils/inferComplianceJurisdiction'
import type {
  customizeReportSchemaType,
  socialMediaSheetSchemaType,
  submitSubjectInfoEditSchemaType,
} from '@/utils/schemas'

import { CompanyInfoEditSheet } from '../../components/CompanyInfoEditSheet'
import { CustomizeReportSheet } from '../../components/CustomizeReportSheet'
import { EditableReviewSection } from '../../components/EditableReviewSection'
import { ProfilePictureField } from '../../components/ProfilePictureField'
import { RunReportStepIndicator } from '../../components/RunReportStepIndicator'
import { SelectFocusedTopicsSheet } from '../../components/SelectFocusedTopicsSheet'
import { SocialMediaSheet } from '../../components/SocialMediaSheet'
import { SubjectInfoEditSheet } from '../../components/SubjectInfoEditSheet'
import { SOCIAL_PLATFORMS } from '../../constants/socialPlatforms'

const MISSING = '—'

const formatLocation = (longParts: {
  city: string
  state: string
  country: string
}) =>
  [longParts.city, longParts.state, longParts.country]
    .filter(Boolean)
    .join(', ') || MISSING

const formatFullName = (
  firstName: string,
  middleName: string,
  lastName: string,
) =>
  [firstName, middleName, lastName].filter(Boolean).join(' ').trim() || MISSING

const formatLanguage = (lang: string, t: (key: string) => string) =>
  lang === 'es'
    ? t('dashboard.runReport.submitReport.languageSpanish')
    : t('dashboard.runReport.submitReport.languageEnglish')

type OpenSheet =
  | 'subjectInfo'
  | 'companyInfo'
  | 'socialMedia'
  | 'focusedTopics'
  | 'customizeReport'
  | null

export const Submit = () => {
  useRunReportGuard(ERunReportStep.Submit)
  const { current, total } = useRunReportWizardStep(ERunReportStep.Submit)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const [agreementChecked, setAgreementChecked] = useState(false)
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null)
  const [isHomeConfirmOpen, setIsHomeConfirmOpen] = useState(false)

  const target = useAppSelector(selectSearchTarget)

  // Local form just to host the ProfilePictureField (it needs a FormProvider).
  // The field's `image` is synced back to redux on change.
  const profilePictureForm = useForm<{ image: string | null }>({
    defaultValues: {
      image:
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null,
    },
  })

  useEffect(() => {
    const subscription = profilePictureForm.watch((values) => {
      const next = values.image ?? null
      const current =
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null
      if (next !== current) {
        dispatch(setSubjectInfo({ ...slice.subjectInfo, image: next }))
      }
    })
    return () => subscription.unsubscribe()
  }, [profilePictureForm, dispatch, slice.subjectInfo])
  const userPdfConfig = useAppSelector(selectUserInfo).pdfConfig
  const selectedWorkspace = useAppSelector(selectSelectedWorkStation)
  const selectedTeam = useAppSelector(selectSelectedTeam)

  const [startNewSearch, { isLoading: isLoadingNewSearch }] =
    useStartNewPersonSearchMutation()
  const [sendEmailWithDocument, { isLoading: isLoadingSendEmailWithDocument }] =
    useSendEmailWithComplianceDocumentMutation()
  const [saveSignedPdf, { isLoading: isLoadingSaveSignedPdf }] =
    useSaveComplianceSignedPdfMutation()

  const isSubmitting =
    isLoadingNewSearch ||
    isLoadingSendEmailWithDocument ||
    isLoadingSaveSignedPdf

  const reportType = slice.selectSearchType.reportType as EReportType
  const hasConsentFlow = slice.useCases.consentSwitchValue
  const isAdverseMedia = reportType === EReportType.AdverseMediaReport
  const isDelegateConsent =
    hasConsentFlow && slice.consent.method === EConsentMethod.Delegate

  const missingRequiredFields = (() => {
    const missing: string[] = []
    if (target.kind === 'company') {
      if (!target.data.name?.trim()) missing.push('companyName')
      if (!target.data.countryLongName?.trim()) missing.push('country')
      return missing
    }
    if (!target.data.firstName?.trim()) missing.push('firstName')
    if (!target.data.lastName?.trim()) missing.push('lastName')
    if (!target.data.countryLongName?.trim()) missing.push('country')
    if (isDelegateConsent && !slice.subjectInfo.email?.trim())
      missing.push('email')
    return missing
  })()

  const canSubmit = agreementChecked && missingRequiredFields.length === 0

  const effectiveReportSetup = slice.reportSetup.useDefaultValue
    ? { ...slice.reportSetup, ...userPdfConfig }
    : slice.reportSetup

  const fullName =
    target.kind === 'company'
      ? target.data.name || MISSING
      : formatFullName(
          target.data.firstName,
          target.data.middleName,
          target.data.lastName,
        )

  const location = formatLocation({
    city: target.data.cityLongName,
    state: target.data.stateLongName,
    country: target.data.countryLongName,
  })

  const consentDisplay =
    slice.consent.method === EConsentMethod.Upload
      ? slice.consent.fileName || MISSING
      : slice.consent.method === EConsentMethod.Delegate
        ? t('dashboard.runReport.submitReport.subjectInputsConsentDelegated')
        : MISSING

  const handleSaveSubjectInfo = (next: submitSubjectInfoEditSchemaType) => {
    dispatch(
      setSubjectInfo({
        ...slice.subjectInfo,
        ...next,
      }),
    )
  }

  const handleSaveCompanyInfo = (next: {
    companyName: string
    companyWebsite: string
    industry: string
    description: string
    countryLongName: string
    countryShortName: string
    stateLongName: string
    stateShortName: string
    cityLongName: string
    cityShortName: string
  }) => {
    dispatch(
      setCompanyInfo({
        ...slice.companyInfo,
        name: next.companyName,
        website: next.companyWebsite,
        industry: next.industry,
        description: next.description ?? '',
        countryLongName: next.countryLongName,
        countryShortName: next.countryShortName,
        stateLongName: next.stateLongName,
        stateShortName: next.stateShortName,
        cityLongName: next.cityLongName,
        cityShortName: next.cityShortName,
      }),
    )
  }

  const handleSaveSocialMedia = (next: socialMediaSheetSchemaType) => {
    dispatch(setSocialMedia(next))
  }

  const handleSaveFocusedTopics = (nextTopics: string[]) => {
    dispatch(setFirstStep({ topics: nextTopics }))
  }

  const handleSaveCustomizeReport = (next: customizeReportSchemaType) => {
    dispatch(
      setReportSetup({
        ...slice.reportSetup,
        ...next,
        useDefaultValue: false,
      }),
    )
  }

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return

    const sortedTopics = slice.firstStep.topics
      ? Object.values(slice.firstStep.topics).sort()
      : []

    const requestPayload = {
      name:
        target.kind === 'subject' ? target.data.firstName : target.data.name,
      lastName: slice.subjectInfo.lastName,
      middleName: slice.subjectInfo.middleName,
      nickname: slice.subjectInfo.alias,
      description: target.data.description ?? '',
      cityShort: target.data.cityShortName,
      cityLong: target.data.cityLongName,
      stateShort: target.data.stateShortName,
      stateLong: target.data.stateLongName,
      countryLong: target.data.countryLongName,
      countryShort: target.data.countryShortName,
      email: slice.subjectInfo.email,
      college: slice.subjectInfo.college,
      phone: slice.subjectInfo.phoneNumber,
      company: slice.subjectInfo.employerName,
      twitterUrl: slice.socialMedia.x,
      facebookUrl: slice.socialMedia.facebook,
      instagramUrl: slice.socialMedia.instagram,
      tikTokUrl: slice.socialMedia.tikTok,
      linkedInUrl: slice.socialMedia.linkedIn,
      pinterestUrl: slice.socialMedia.pinterest,
      redditUrl: slice.socialMedia.reddit,
      ageOrDateOfBirth: slice.subjectInfo.dob,
      pdfCoreFindings: effectiveReportSetup.pdfCoreFindings,
      pdfFlaggedBehaviors: effectiveReportSetup.pdfFlaggedBehaviors,
      pdfLanguage: effectiveReportSetup.pdfLanguage,
      pdfNegativePosts: effectiveReportSetup.pdfNegativePosts,
      pdfNeutralPosts: effectiveReportSetup.pdfNeutralPosts,
      pdfPositivePosts: effectiveReportSetup.pdfPositivePosts,
      pdfSentimentAnalysis: effectiveReportSetup.pdfSentimentAnalysis,
      pdfSocialMediaAccounts: effectiveReportSetup.pdfSocialMediaAccounts,
      pdfWebSearchPosts: effectiveReportSetup.pdfWebSearchPosts,
      pdfPersonalityAndSkills: effectiveReportSetup.pdfPersonalityAndSkills,
      pdfEducationAndCareer: effectiveReportSetup.pdfEducationAndCareer,
      pdfDigitalFootprint: effectiveReportSetup.pdfDigitalFootprint,
      pdfContentRadar: effectiveReportSetup.pdfContentRadar,
      pdfUnverifiedDigitalFootprint:
        effectiveReportSetup.pdfUnverifiedDigitalFootprint,
      topics: sortedTopics,
      reportType,
      useCase: slice.useCases.selectedUseCaseId,
      optionalCompliance: hasConsentFlow,
      website: slice.companyInfo.website,
      industry: slice.companyInfo.industry,
      picture: slice.subjectInfo.image,
      // A company search is an organization for the find-module; a person search sends nothing new.
      subjectKind:
        target.kind === 'company' ? ('organization' as const) : undefined,
    }

    try {
      const filteredPayload = filterEmptyFields(requestPayload, 'lastName')

      const response = await startNewSearch({
        ...filteredPayload,
        workspaceId: selectedWorkspace?._id,
        teamId: selectedTeam?._id,
      }).unwrap()
      const { info } = response

      if (
        slice.consent.file !== null &&
        slice.consent.file !== undefined &&
        slice.consent.file
      ) {
        const formData = base64ToFormData(slice.consent.file, 'signed.pdf')
        await saveSignedPdf({ id: info.id, document: formData }).unwrap()
      } else if (hasConsentFlow) {
        await sendEmailWithDocument({
          id: info.id,
          documentToSend: inferComplianceJurisdiction({
            countryShortName: target.data.countryShortName,
            stateLongName: target.data.stateLongName,
          }),
          businessDocument: slice.consent.businessLocationValue,
          email: slice.subjectInfo.email,
        }).unwrap()
      }

      if (reportType === EReportType.AiReport) {
        const params = new URLSearchParams({
          subjectId: info.subjectId,
          reportId: info.id,
        })
        navigate(`${AppRoutes.generatingReport}?${params.toString()}`)
        return
      }

      const variant = hasConsentFlow
        ? ESearchStartedVariant.AwaitingConsent
        : ESearchStartedVariant.Generating
      navigate(`${AppRoutes.runReportSearchStarted}?variant=${variant}`)
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setExtra('requestPayload', requestPayload)
        Sentry.captureException(error)
      })
      toast.error(t('feedback.toast.errorOnSearch'))
    }
  }

  const requiredSuffix = ` ${t(
    'dashboard.runReport.submitReport.requiredSuffix',
  )}`
  const isFullNameMissing =
    target.kind === 'company'
      ? !target.data.name?.trim()
      : !target.data.firstName?.trim() || !target.data.lastName?.trim()
  const isLocationMissing = !target.data.countryLongName?.trim()
  const isEmailMissing = isDelegateConsent && !slice.subjectInfo.email?.trim()

  const subjectRows: Array<{ label: string; value: string; isLink?: boolean }> =
    target.kind === 'company'
      ? [
          {
            label:
              t('dashboard.runReport.submitReport.subjectInputsFullName') +
              (isFullNameMissing ? requiredSuffix : ''),
            value: fullName,
          },
          {
            label:
              t('dashboard.runReport.submitReport.subjectInputsLocation') +
              (isLocationMissing ? requiredSuffix : ''),
            value: location,
          },
          {
            label: t('dashboard.runReport.companyInfoFields.companyWebsite'),
            value: slice.companyInfo.website || MISSING,
          },
          {
            label: t('dashboard.runReport.companyInfoFields.industry'),
            value: slice.companyInfo.industry || MISSING,
          },
          {
            label: t('dashboard.runReport.companyInfoFields.description'),
            value: slice.companyInfo.description || MISSING,
          },
        ]
      : [
          {
            label:
              t('dashboard.runReport.submitReport.subjectInputsFullName') +
              (isFullNameMissing ? requiredSuffix : ''),
            value: fullName,
          },
          {
            label: t('dashboard.runReport.submitReport.subjectInputsAlias'),
            value: slice.subjectInfo.alias || MISSING,
          },
          {
            label: t('dashboard.runReport.submitReport.subjectInputsAge'),
            value: slice.subjectInfo.dob || MISSING,
          },
          {
            label:
              t('dashboard.runReport.submitReport.subjectInputsLocation') +
              (isLocationMissing ? requiredSuffix : ''),
            value: location,
          },
          {
            label:
              t('dashboard.runReport.submitReport.subjectInputsEmail') +
              (isEmailMissing ? requiredSuffix : ''),
            value: slice.subjectInfo.email || MISSING,
          },
          {
            label: t('dashboard.runReport.submitReport.subjectInputsPhone'),
            value: slice.subjectInfo.phoneNumber || MISSING,
          },
          {
            label: t('dashboard.runReport.submitReport.subjectInputsEmployer'),
            value: slice.subjectInfo.employerName || MISSING,
          },
          {
            label: t('dashboard.runReport.submitReport.subjectInputsSchool'),
            value: slice.subjectInfo.college || MISSING,
          },
        ]

  if (hasConsentFlow) {
    subjectRows.push({
      label: t('dashboard.runReport.submitReport.subjectInputsConsent'),
      value: consentDisplay,
      isLink: slice.consent.method === EConsentMethod.Upload,
    })
  }

  const topics = slice.firstStep.topics ?? []

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      useHeaderBackButton={false}
      useHomeIcon
      onClickBack={() => setIsHomeConfirmOpen(true)}
      hideHeaderOnMobile
      contentClassName="p-0"
    >
      <div className="flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col lg:h-full lg:flex-row lg:gap-6 lg:overflow-hidden lg:p-8">
        <div className="hidden h-full flex-col lg:flex lg:flex-1">
          <div className="flex flex-1 flex-col justify-center gap-5 px-3 py-3">
            <RunReportStepIndicator current={current} total={total} />
            <h2 className="text-[32px] font-medium leading-tight text-neutralColor">
              {t('dashboard.runReport.submitReport.title')}
            </h2>

            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={agreementChecked}
                onCheckedChange={(checked) =>
                  setAgreementChecked(checked === true)
                }
                className="mt-0.5 h-6 w-6 rounded-md"
              />
              <span className="text-sm leading-normal text-neutralColor80">
                {t('dashboard.runReport.submitReport.agreement')}
              </span>
            </label>

            <div>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                isLoading={isSubmitting}
              >
                {t('dashboard.runReport.submitReport.sendRequest')}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 lg:h-full lg:overflow-hidden lg:p-0">
          <div className="flex flex-col gap-5 rounded-2xl bg-baseColorHighlight p-5 lg:h-full lg:min-h-0 lg:overflow-y-auto">
            <FormProvider {...profilePictureForm}>
              <ProfilePictureField />
            </FormProvider>

            <EditableReviewSection
              title={t('dashboard.runReport.submitReport.subjectInputsTitle')}
              onEdit={() =>
                setOpenSheet(
                  target.kind === 'company' ? 'companyInfo' : 'subjectInfo',
                )
              }
            >
              <div className="flex flex-col gap-4">
                {subjectRows.map(({ label, value, isLink }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-3 text-base"
                  >
                    <span className="text-neutralColor">{label}</span>
                    <span
                      className={cn(
                        'truncate text-right',
                        isLink ? 'text-dynamicBlue' : 'text-neutralColor60',
                      )}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </EditableReviewSection>

            {!isAdverseMedia && (
              <>
                <div className="border-t border-neutralColor5" />

                <EditableReviewSection
                  title={t('dashboard.runReport.submitReport.socialMediaTitle')}
                  onEdit={() => setOpenSheet('socialMedia')}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    {SOCIAL_PLATFORMS.map(({ key, Icon }) => {
                      const isFilled = Boolean(slice.socialMedia[key]?.trim())
                      return (
                        <span
                          key={key}
                          className={cn(
                            'flex h-[46px] w-[46px] items-center justify-center rounded-full',
                            isFilled
                              ? 'bg-alertBlueBase text-alertBlueSecondary'
                              : 'border border-dashed border-neutralColor5 text-neutralColor60',
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                      )
                    })}
                  </div>
                </EditableReviewSection>
              </>
            )}

            <div className="border-t border-neutralColor5" />

            <EditableReviewSection
              title={t('dashboard.runReport.submitReport.focusedTopicsTitle')}
              onEdit={() => setOpenSheet('focusedTopics')}
            >
              {topics.length === 0 ? (
                <p className="text-base text-neutralColor60">{MISSING}</p>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  {topics.map((topicName) => (
                    <Chip
                      key={topicName}
                      variant={ChipVariant.SelectedTopic}
                      size={ChipSize.Tag}
                      text={topicName}
                    />
                  ))}
                </div>
              )}
            </EditableReviewSection>

            <div className="border-t border-neutralColor5" />

            <EditableReviewSection
              title={t('dashboard.runReport.submitReport.customizeReportTitle')}
              onEdit={() => setOpenSheet('customizeReport')}
            >
              <div className="flex flex-col gap-2">
                <p className="text-base font-bold text-dynamicBlue">
                  {t(
                    'dashboard.runReport.submitReport.customizeReportDefaultLanguage',
                  )}{' '}
                  {formatLanguage(effectiveReportSetup.pdfLanguage, t)}
                </p>
                <p className="text-base text-neutralColor">
                  {t(
                    'dashboard.runReport.submitReport.customizeReportDescription',
                  )}
                </p>
              </div>
            </EditableReviewSection>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 bg-baseColor px-4 pb-4 pt-3 lg:hidden">
          <h2 className="text-xl font-medium leading-tight text-neutralColor">
            {t('dashboard.runReport.submitReport.title')}
          </h2>
          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox
              checked={agreementChecked}
              onCheckedChange={(checked) =>
                setAgreementChecked(checked === true)
              }
              className="mt-0.5 h-5 w-5 rounded-md"
            />
            <span className="text-xs leading-normal text-neutralColor80">
              {t('dashboard.runReport.submitReport.agreement')}
            </span>
          </label>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            isLoading={isSubmitting}
          >
            {t('dashboard.runReport.submitReport.sendRequest')}
          </Button>
        </div>
      </div>

      <SubjectInfoEditSheet
        open={openSheet === 'subjectInfo'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        isEmailRequired={isDelegateConsent}
        value={{
          firstName: slice.subjectInfo.firstName,
          middleName: slice.subjectInfo.middleName,
          lastName: slice.subjectInfo.lastName,
          alias: slice.subjectInfo.alias,
          dob: slice.subjectInfo.dob,
          email: slice.subjectInfo.email,
          phoneNumber: slice.subjectInfo.phoneNumber,
          employerName: slice.subjectInfo.employerName,
          college: slice.subjectInfo.college,
          countryLongName: slice.subjectInfo.countryLongName,
          countryShortName: slice.subjectInfo.countryShortName,
          stateLongName: slice.subjectInfo.stateLongName,
          stateShortName: slice.subjectInfo.stateShortName,
          cityLongName: slice.subjectInfo.cityLongName,
          cityShortName: slice.subjectInfo.cityShortName,
        }}
        onSave={handleSaveSubjectInfo}
        consentFile={hasConsentFlow ? slice.consent.file : undefined}
        consentFileName={hasConsentFlow ? slice.consent.fileName : undefined}
      />

      <CompanyInfoEditSheet
        open={openSheet === 'companyInfo'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        value={{
          companyName: slice.companyInfo.name,
          companyWebsite: slice.companyInfo.website,
          industry: slice.companyInfo.industry,
          description: slice.companyInfo.description ?? '',
          countryLongName: slice.companyInfo.countryLongName,
          countryShortName: slice.companyInfo.countryShortName,
          stateLongName: slice.companyInfo.stateLongName,
          stateShortName: slice.companyInfo.stateShortName,
          cityLongName: slice.companyInfo.cityLongName,
          cityShortName: slice.companyInfo.cityShortName,
        }}
        onSave={handleSaveCompanyInfo}
      />

      <SocialMediaSheet
        open={openSheet === 'socialMedia'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        value={slice.socialMedia}
        onSave={handleSaveSocialMedia}
      />

      <SelectFocusedTopicsSheet
        open={openSheet === 'focusedTopics'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        value={slice.firstStep.topics ?? []}
        onSave={handleSaveFocusedTopics}
      />

      <CustomizeReportSheet
        open={openSheet === 'customizeReport'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        value={{
          pdfLanguage: effectiveReportSetup.pdfLanguage,
          pdfCoreFindings: effectiveReportSetup.pdfCoreFindings,
          pdfSocialMediaAccounts: effectiveReportSetup.pdfSocialMediaAccounts,
          pdfSentimentAnalysis: effectiveReportSetup.pdfSentimentAnalysis,
          pdfFlaggedBehaviors: effectiveReportSetup.pdfFlaggedBehaviors,
          pdfPositivePosts: effectiveReportSetup.pdfPositivePosts,
          pdfNeutralPosts: effectiveReportSetup.pdfNeutralPosts,
          pdfNegativePosts: effectiveReportSetup.pdfNegativePosts,
          pdfWebSearchPosts: effectiveReportSetup.pdfWebSearchPosts,
          pdfPersonalityAndSkills: effectiveReportSetup.pdfPersonalityAndSkills,
          pdfEducationAndCareer: effectiveReportSetup.pdfEducationAndCareer,
          pdfDigitalFootprint: effectiveReportSetup.pdfDigitalFootprint,
          pdfContentRadar: effectiveReportSetup.pdfContentRadar,
          pdfUnverifiedDigitalFootprint:
            effectiveReportSetup.pdfUnverifiedDigitalFootprint,
        }}
        onSave={handleSaveCustomizeReport}
      />

      <ResponsiveModal
        open={isHomeConfirmOpen}
        onOpenChange={setIsHomeConfirmOpen}
      >
        <ResponsiveModalContent className="sm:max-w-[560px] bg-baseColor border-0 p-8 sm:rounded-2xl gap-6">
          <ResponsiveModalHeader className="space-y-0 gap-3">
            <ResponsiveModalTitle>
              {t('dashboard.runReport.submitReport.leaveConfirmTitle')}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {t('dashboard.runReport.submitReport.leaveConfirmDescription')}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <div className="flex gap-3 max-md:w-full md:justify-end">
            <Button
              variant="secondary"
              onClick={() => setIsHomeConfirmOpen(false)}
              className="max-md:flex-1"
            >
              {t('dashboard.runReport.submitReport.leaveConfirmCancel')}
            </Button>
            <Button
              onClick={() => navigate(AppRoutes.dashboard)}
              className="max-md:flex-1"
            >
              {t('dashboard.runReport.submitReport.leaveConfirmConfirm')}
            </Button>
          </div>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </WeScreen>
  )
}
